import { NgClass, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GoogleAnalyticsService } from '@lx/shared/google-analytics';
import { getCompany, getConfiguration } from '@lx/shared/tokens';
import { COMPANIES } from '@lx/shared/types';
import { CardModule } from 'primeng/card';
import { TabViewModule } from 'primeng/tabview';
import { DragScrollDirective } from '../../../directives';
import { GoogleAnalyticsEventEnum } from '../../../google-analytics';
import { QuoteToolLiteStore } from '../../../store';
import { CoursePayload } from '../../../types';
import { AcademicYearAbroadComponent } from '../../academic-year-abroad/academic-year-abroad.component';
import { LanguageCampsComponent } from '../../language-camps/language-camps.component';
import { LanguageSchoolsComponent } from '../../language-schools/language-schools.component';
import { OnlineCoursesComponent } from '../../online-courses/online-courses.component';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';
import { OnlineCoursesWayfinderBannerComponent } from '../../online-courses-wayfinder-banner/online-courses-wayfinder-banner.component';

@Component({
  standalone: true,
  imports: [
    TabViewModule,
    NgTemplateOutlet,
    CardModule,
    LanguageSchoolsComponent,
    LanguageCampsComponent,
    DragScrollDirective,
    OnlineCoursesComponent,
    NgClass,
    NgForOf,
    NgIf,
    AcademicYearAbroadComponent,
    OnlineCoursesWayfinderBannerComponent,
  ],
  selector: 'lx-course-step',
  templateUrl: './course-step.component.html',
  styleUrls: ['./course-step.component.scss'],
})
export class CourseStepComponent implements AfterViewInit, OnInit {
  @ViewChild('adultsHeaderTab') adultsHeaderTab: TemplateRef<unknown>;
  @ViewChild('adultsTab') adultsTab: TemplateRef<unknown>;
  @ViewChild('juniorsHeaderTab') juniorsHeaderTab: TemplateRef<unknown>;
  @ViewChild('juniorsTab') juniorsTab: TemplateRef<unknown>;
  @ViewChild('onlinedHeaderTab') onlinedHeaderTab: TemplateRef<unknown>;
  @ViewChild('onlineTab') onlineTab: TemplateRef<unknown>;
  @ViewChild('ayaHeaderTab') ayaHeaderTab: TemplateRef<unknown>;
  @ViewChild('ayaTab') ayaTab: TemplateRef<unknown>;

  // The tabs to display
  tabs: { header: TemplateRef<unknown>; body: TemplateRef<unknown> }[] = [];

  activeFlow: Flow = Flows.Adult;
  activeIndex = 0;
  enabledOnlineCourses: boolean;
  enabledAyaCourses: boolean;

  readonly company = getCompany();

  private activeFlowTabsConfig = [];

  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly configuration = getConfiguration();
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  ngOnInit(): void {
    this.enabledOnlineCourses = <boolean>this.configuration.QUOTE_TOOL_LITE_ENABLE_ONLINE_COURSES;
    this.enabledAyaCourses = <boolean>this.configuration.QUOTE_TOOL_LITE_ENABLE_AYA_COURSES;
  }

  ngAfterViewInit(): void {
    // Fill the tabs accordingly with the company. For kaplan, adults will be
    // the first tab. On the contrary, in Alpadia, juniors will be the first tab.

    this.quoteToolLiteStore.activeFlow$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeFlow) => {
      this.activeFlow = activeFlow;
      this.prepareTabs(activeFlow);
      this.changeDetector.detectChanges();
    });
  }

  setActiveFlow(index: number) {
    this.activeFlow = this.activeFlowTabsConfig.find((tab) => tab.index === index).activeFlow;
    this.quoteToolLiteStore.updateState({ activeFlow: this.activeFlow });
  }

  updateValidity(valid: boolean) {
    this.quoteToolLiteStore.updateValidityOfCurrentStep(valid);
  }

  saveCoursePayload(coursePayload: CoursePayload) {
    this.quoteToolLiteStore.updateState(coursePayload);
  }

  trackClickEvent() {
    this.googleAnalyticsService.trackClickEvent(GoogleAnalyticsEventEnum.QuoteProgramTypeChange);
  }

  private prepareTabs(activeFlow: Flow) {
    const allAvailableTabs = this.getAllAvailableTabs();
    const visibleTabs = allAvailableTabs.filter((tab) => tab.enabled);
    this.tabs = visibleTabs.map((tab) => ({ header: tab.header, body: tab.body }));

    const activeFlowIndex = {};
    this.activeFlowTabsConfig = [];

    visibleTabs.forEach((tab, index) => {
      activeFlowIndex[tab.flow] = index;
      this.activeFlowTabsConfig.push({ index, activeFlow: tab.flow });
    });

    this.activeIndex = activeFlowIndex[activeFlow];
  }

  private getAllAvailableTabs() {
    switch (this.company) {
      case COMPANIES.KAPLAN:
      case COMPANIES.POSITIVELY_LANGUAGES: {
        return [
          { flow: Flows.Adult, enabled: true, header: this.adultsHeaderTab, body: this.adultsTab },
          { flow: Flows.Junior, enabled: true, header: this.juniorsHeaderTab, body: this.juniorsTab },
          { flow: Flows.Aya, enabled: this.enabledAyaCourses, header: this.ayaHeaderTab, body: this.ayaTab },
          { flow: Flows.Online, enabled: this.enabledOnlineCourses, header: this.onlinedHeaderTab, body: this.onlineTab },
        ];
      }
      case COMPANIES.ALPADIA: {
        return [
          { flow: Flows.Junior, enabled: true, header: this.juniorsHeaderTab, body: this.juniorsTab },
          { flow: Flows.Adult, enabled: true, header: this.adultsHeaderTab, body: this.adultsTab },
          { flow: Flows.Aya, enabled: this.enabledAyaCourses, header: this.ayaHeaderTab, body: this.ayaTab },
        ];
      }
    }
  }
}
